import { useMemo, useCallback, useState, useDeferredValue } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import * as Sentry from '@sentry/react'

import { useIdeaComposer } from '~publish/components/IdeaManagementRouter/hooks'
import { useChannels } from '~publish/components/MainSidebar/components/ChannelsSection/useChannels'
import { useSavedViewsQuery } from '~publish/components/MainSidebar/components/SavedViewsSection/hooks'
import type { SavedView } from '~publish/components/MainSidebar/components/SavedViewsSection/hooks'
import { useAllChannelsUrl } from '~publish/components/MainSidebar/hooks/useAllChannelsUrl'
import { useCommentsUrl } from '~publish/components/MainSidebar/hooks/useCommentsUrl'
import {
  useIsLegacyPlan,
  useAccountId,
  useOrganizationId,
  useIsFreeUser,
  useCurrentOrganization,
  useIsOneBufferOrg,
} from '~publish/contexts/AccountContext'
import { useCommunityCountsContext } from '~publish/contexts/CommunityCountsContext'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import { useTagsQuery } from '~publish/hooks/useTags'
import { useChannelGroups } from '~publish/pages/Settings/ChannelGroups/hooks/useChannelGroups'
import { useCanAccessTeamFeatures } from '~publish/pages/Settings/Team/hooks/useCanAccessTeamFeatures'

import { useContextActions, useCurrentContext } from './useContextActions'
import { useFrecency } from './useFrecency'
import { useRecentItems } from './useRecentItems'
import { buildChannelActions } from '../actions/channelActions'
import { buildChannelGroupActions } from '../actions/channelGroupActions'
import { buildConnectChannelActions } from '../actions/connectChannelActions'
import { buildCreationActions } from '../actions/creationActions'
import { buildHelpActions } from '../actions/helpActions'
import { buildNavigationActions } from '../actions/navigationActions'
import { buildSavedViewActions } from '../actions/savedViewActions'
import { buildSettingsActions } from '../actions/settingsActions'
import { buildTagActions } from '../actions/tagActions'
import { buildTeamActions } from '../actions/teamActions'
import type { CommandBarAction, FrecencyStorage } from '../actions/types'

/**
 * Options for the useActions hook.
 */
interface UseActionsOptions {
  /**
   * Called before navigating to a saved view so the caller can fire the
   * `Saved View Loaded` analytics event with the right source.
   */
  onSavedViewSelected: (view: SavedView) => void
}

/**
 * Return value from the useActions hook.
 */
interface UseActionsResult {
  /** All available actions (filtered by context) */
  actions: CommandBarAction[]
  /** Whether actions are still loading */
  loading: boolean
  /** Execute an action and track its use */
  executeAction: (action: CommandBarAction) => Promise<void>
  /** Frecency data for custom search scoring */
  frecencyData: FrecencyStorage
  /** Callback to update the search query from CommandBarContent */
  setSearchQuery: (query: string) => void
}

/**
 * Main hook that combines all action builders and provides
 * a unified list of actions for the command bar.
 */
export function useActions({
  onSavedViewSelected,
}: UseActionsOptions): UseActionsResult {
  const navigate = useNavigate()
  const location = useLocation()
  const { channels, loading: channelsLoading } = useChannels()
  const { tags, loading: tagsLoading } = useTagsQuery()
  const { savedViews } = useSavedViewsQuery()
  const accountId = useAccountId()
  const organizationId = useOrganizationId()
  const { channelGroups, loading: channelGroupsLoading } = useChannelGroups({
    accountId: accountId ?? '',
    organizationId: organizationId ?? '',
  })
  const isLegacyPlan = useIsLegacyPlan()
  const isFreeUser = useIsFreeUser()
  const { createNewPostInComposer } = usePostComposer()
  const { createIdeaWithComposer } = useIdeaComposer()
  const { canAccessTeamFeatures } = useCanAccessTeamFeatures()
  const currentOrganization = useCurrentOrganization()
  const isOneBufferOrganization = useIsOneBufferOrg()
  // The same inputs the Settings sidebar gates its items on, so ⌘K never
  // offers a settings page the sidebar hides.
  const showBetaFeatures = Boolean(
    !currentOrganization || isOneBufferOrganization,
  )
  const organizationActions = currentOrganization?.allowedActions
  const hasConnectedChannels = (currentOrganization?.channels.length ?? 0) > 0

  // Search query state - lifted from CommandBarContent so action builders can use it
  const [searchQuery, setSearchQuery] = useState('')
  // Defer the search query to avoid expensive action rebuilding on every keystroke
  const deferredSearchQuery = useDeferredValue(searchQuery)

  // Single channel mode detection
  const isSingleChannelMode = channels.length === 1
  const singleChannel = isSingleChannelMode ? channels[0] : undefined

  // URL hooks
  const singleChannelId = channels.length === 1 ? channels[0]?.id : undefined
  const allChannelsUrl = useAllChannelsUrl(singleChannelId)
  const commentsUrl = useCommentsUrl(singleChannelId)

  // Current context for context-aware actions
  const currentContext = useCurrentContext()

  const { getChannelCount: getUnrepliedCommentsCount } =
    useCommunityCountsContext()

  // Frecency and recent items hooks
  const { recordUse, sortByFrecency, frecencyData } = useFrecency()
  const { addRecentItem, getRecentActions } = useRecentItems()

  // Build query-independent actions (don't change based on search query)
  const staticActions = useMemo(() => {
    const actions: CommandBarAction[] = []

    actions.push(
      ...buildNavigationActions({
        navigate,
        allChannelsUrl,
        commentsUrl,
        isLegacyPlan,
      }),
    )

    actions.push(
      ...buildTagActions({
        tags,
        navigate,
        currentPathname: location.pathname,
        currentSearch: location.search,
      }),
    )

    actions.push(
      ...buildChannelGroupActions({
        channelGroups,
        navigate,
        currentPathname: location.pathname,
      }),
    )

    actions.push(
      ...buildSavedViewActions({
        savedViews,
        navigate,
        onSelect: onSavedViewSelected,
        validChannelIds: channels.map((channel) => channel.id),
      }),
    )

    actions.push(
      ...buildCreationActions({
        createNewPostInComposer,
        openIdeaComposer: () =>
          createIdeaWithComposer({
            source: 'publish-commandBar-newIdea-createIdea-1',
          }),
        isLegacyPlan,
        singleChannelName: singleChannel?.name,
        singleChannelId: singleChannel?.id,
      }),
    )

    actions.push(
      ...buildTeamActions({
        navigate,
        isFreeUser,
      }),
    )

    actions.push(...buildHelpActions())

    return actions
  }, [
    navigate,
    location.pathname,
    location.search,
    allChannelsUrl,
    commentsUrl,
    isLegacyPlan,
    isFreeUser,
    tags,
    channelGroups,
    savedViews,
    channels,
    onSavedViewSelected,
    createNewPostInComposer,
    createIdeaWithComposer,
    singleChannel,
  ])

  // Build query-dependent actions (change based on search query for display modes)
  // Uses deferredSearchQuery to avoid expensive rebuilding on every keystroke
  const queryDependentActions = useMemo(() => {
    const actions: CommandBarAction[] = []

    // Channel actions - uses query for collapsed/expanded/filtered display modes
    actions.push(
      ...buildChannelActions({
        channels,
        navigate,
        currentContext,
        isSingleChannelMode,
        query: deferredSearchQuery,
        getUnrepliedCommentsCount,
      }),
    )

    // Connect channel action - uses query for service-specific suggestions
    actions.push(
      ...buildConnectChannelActions({
        navigate,
        query: deferredSearchQuery,
      }),
    )

    // Settings actions - uses query for collapsed/expanded behavior
    actions.push(
      ...buildSettingsActions({
        navigate,
        canAccessTeamFeatures,
        showBetaFeatures,
        organizationActions,
        hasConnectedChannels,
        query: deferredSearchQuery,
      }),
    )

    return actions
  }, [
    channels,
    navigate,
    currentContext,
    isSingleChannelMode,
    deferredSearchQuery,
    canAccessTeamFeatures,
    showBetaFeatures,
    organizationActions,
    hasConnectedChannels,
    getUnrepliedCommentsCount,
  ])

  // Combine all actions
  const allActions = useMemo(() => {
    return [...staticActions, ...queryDependentActions]
  }, [staticActions, queryDependentActions])

  // Filter actions by context
  const contextFilteredActions = useContextActions(allActions)

  // Sort by frecency and add recent section
  const sortedActions = useMemo(() => {
    const sorted = sortByFrecency(contextFilteredActions)

    // Get recent actions and add them to the front
    const recentActions = getRecentActions(sorted)

    // Filter out recent items from the main list (they're shown in recent section)
    const recentIds = new Set(
      recentActions.map((a) => a.id.replace('recent-', '')),
    )
    const nonRecentActions = sorted.filter((a) => !recentIds.has(a.id))

    return [...recentActions, ...nonRecentActions]
  }, [contextFilteredActions, sortByFrecency, getRecentActions])

  // Execute action and track usage
  const executeAction = useCallback(
    async (action: CommandBarAction) => {
      // Track the use for frecency (unless excluded)
      if (!action.excludeFromFrecency) {
        const originalId = action.id.replace('recent-', '')
        recordUse(originalId)
        addRecentItem(originalId)
      }

      try {
        await action.perform()
      } catch (error) {
        Sentry.withScope((scope) => {
          scope.setTag('source', 'command-bar')
          scope.setExtra('actionId', action.id)
          Sentry.captureException(error)
        })
        throw error
      }
    },
    [recordUse, addRecentItem],
  )

  return {
    actions: sortedActions,
    loading: channelsLoading || tagsLoading || channelGroupsLoading,
    executeAction,
    frecencyData,
    setSearchQuery,
  }
}
